/**
 * ActionKeys are shared by Actions and Reducers and can lead to cases where
 * we form dependency cycles between those two. Extracting them here
 * helps prevent build issues. So far only the NotebookActionKeys resulted in
 * build cycles.
 */

export enum AcceleratorQuotaStatsActionKey {
  SET_ACCELERATOR_QUOTA_STATS = "SET_ACCELERATOR_QUOTA_STATS",
  INCREMENT_ACCELERATOR_QUOTA_STATS = "INCREMENT_ACCELERATOR_QUOTA_STATS",
}

export enum EnableLspActionKey {
  SET_ENABLE_LSP = "SET_ENABLE_LSP",
}

export enum BannerActionKey {
  DISMISS_BANNER = "DISMISS_BANNER",
}

export enum EditorActionKey {
  EDITOR_ERROR = "EDITOR_ERROR",
  SET_EDITOR_STATUS = "SET_EDITOR_STATUS",
  SET_EDITOR_STATUS_MESSAGE = "SET_EDITOR_STATUS_MESSAGE",
  SET_EDITOR_KERNEL_IS_RUNNING = "SET_EDITOR_KERNEL_IS_RUNNING",
  SET_EDITOR_SELECTED_CELL_TYPE = "SET_EDITOR_SELECTED_CELL_TYPE",
  SET_EDITOR_MENU = "SET_EDITOR_MENU",
  PUSH_EDITOR_MENU = "PUSH_EDITOR_MENU",
  POP_EDITOR_MENU = "POP_EDITOR_MENU",
  CLEAR_EDITOR_MENU = "CLEAR_EDITOR_MENU",
  SET_NONFATAL_ERROR_MESSAGE = "SET_NONFATAL_ERROR_MESSAGE",
  SET_EDITOR_CAIP_NUDGE_TRIGGER_EVENT = "SET_EDITOR_CAIP_NUDGE_TRIGGER_EVENT",
}

export enum NotebookActionKey {
  SET_TITLE = "SET_TITLE",
  SET_PERSISTENCE = "SET_PERSISTENCE",
  SET_SLUG = "SET_SLUG",
  SET_KERNEL = "SET_KERNEL",
  SET_RUN = "SET_RUN",
  SET_STARTED_CANCELED = "SET_STARTED_CANCELED",
  STOP_RUN = "STOP_RUN",
  SET_CURRENT_LANGUAGE_ID = "SET_CURRENT_LANGUAGE_ID",
  SET_DOCKER_IMAGES = "SET_DOCKER_IMAGES",
  SET_DOCKER_IMAGE = "SET_DOCKER_IMAGE",
  SET_DOCKER_PINNING_TYPE = "SET_DOCKER_PINNING_TYPE",
  SET_IS_INTERNET_ENABLED = "SET_IS_INTERNET_ENABLED",
  SET_IS_INTERNET_ENABLED_BACKEND = "SET_IS_INTERNET_ENABLED_BACKEND",
  SET_BACKEND_UNIT_NAME = "SET_BACKEND_UNIT_NAME",
  SET_ACCELERATOR = "SET_ACCELERATOR",
  SET_SAVE_STATUS_SAVED = "SET_SAVE_STATUS_SAVED",
  SET_SAVE_STATUS_ERROR = "SET_SAVE_STATUS_ERROR",
  SET_JUPYTER_PROXY_URL = "SET_JUPYTER_PROXY_URL",
  SET_DISABLE_COMMENTS = "SET_DISABLE_COMMENTS",
  SET_PRIVACY = "SET_PRIVACY",
  SET_CAN_TOGGLE_VISIBILITY = "SET_CAN_TOGGLE_VISIBILITY",
  SET_DATA_SOURCES = "SET_DATA_SOURCES",
  SET_REMOVED_DATA_SOURCE = "SET_REMOVED_DATA_SOURCE",
  ADD_DATA_SOURCE = "ADD_DATA_SOURCE",
  REMOVE_DATA_SOURCE = "REMOVE_DATA_SOURCE",
  SET_DATA_SOURCE_REFERENCES = "SET_DATA_SOURCE_REFERENCES",
  SET_BEST_AND_LATEST_SCORES = "SET_BEST_AND_LATEST_SCORES",
  SET_SUBMISSION_LIMIT_INFO = "SET_SUBMISSION_LIMIT_INFO",
  SET_FIREBASE_SESSION_ID = "SET_FIREBASE_SESSION_ID",
  SET_QUEUE_POSITION = "SET_QUEUE_POSITION",
  SET_KERNEL_ID_PATH = "SET_KERNEL_ID_PATH",
  SET_DOCKER_IMAGE_TAG = "SET_DOCKER_IMAGE_TAG",
  SET_WORKER_POOL_NAME = "SET_WORKER_POOL_NAME",
  SET_COLLABORATORS = "SET_COLLABORATORS",
  SET_OWNER = "SET_OWNER",
  SET_DRAFT_SPEC = "SET_DRAFT_SPEC",
  SET_KERNEL_SPEC = "SET_KERNEL_SPEC",
  SET_IPYNB = "SET_IPYNB",
  SET_TAGS = "SET_TAGS",
  SET_USE_NEW_BACKEND = "SET_USE_NEW_BACKEND",
  SET_SHOW_ACCELERATOR_UNUSED_CALLOUT = "SET_SHOW_ACCELERATOR_UNUSED_CALLOUT",
  SET_SHOW_KERNEL_FORKED_FROM_PAID_TIER_WARNING = "SET_SHOW_KERNEL_FORKED_FROM_PAID_TIER_WARNING",
  SET_SHOW_KERNEL_DRAFT_FAILED_SAVE_WARNING = "SET_SHOW_KERNEL_DRAFT_FAILED_SAVE_WARNING",
  SET_SCHEDULE = "SET_SCHEDULE",
  SET_OUTPUT_TO_DATASET_ENABLED = "SET_OUTPUT_TO_DATASET_ENABLED",
  SET_OUTPUT_DATASET = "SET_OUTPUT_DATASET",
  SET_FOCUS_ON_SCHEDULE = "SET_FOCUS_ON_SCHEDULE",
}

export enum ConsoleActionKey {
  APPEND_CONSOLE = "APPEND_CONSOLE",
  /** Action to clear all lines from the console. */
  CLEAR_CONSOLE = "CLEAR_CONSOLE",
  SET_CONSOLE_EXPANDED = "SET_CONSOLE_EXPANDED",
}

export enum BatchedRunActionKey {
  SET_BATCHED_RUN = "SET_BATCHED_RUN",
  REMOVE_BATCHED_RUN = "REMOVE_BATCHED_RUN",
  SET_BATCHED_RUN_STATUS = "SET_BATCHED_RUN_STATUS",
  SET_BATCHED_RUN_ERROR_MESSAGE = "SET_BATCHED_RUN_ERROR_MESSAGE",
  SET_SHOW_BATCHED_RUN_PROGRESS_SNACKBAR = "SET_SHOW_BATCHED_RUN_PROGRESS_SNACKBAR",
}

export enum FeatureFlagActionKey {
  SET_FEATURE_FLAGS = "SET_FEATURE_FLAGS",
}

export enum RunActionKey {
  SET_RUN_STATUS = "SET_RUN_STATUS",
  SET_RUN_STATUS_BATCHED = "SET_RUN_STATUS_BATCHED",
  FETCH_RUN_SUCCESS = "FETCH_RUN_SUCCESS",
  UPDATE_RESOURCE_USAGE = "UPDATE_RESOURCE_USAGE",
  UPDATE_RUN_LOGS = "UPDATE_RUN_LOGS",
  UPDATE_STD_OUT = "UPDATE_STD_OUT",
  UPDATE_STD_ERR = "UPDATE_STD_ERR",
  SET_DATASET_DOWNLOAD_PROGRESS_MSG = "SET_DATASET_DOWNLOAD_PROGRESS_MSG",
}

export enum ThemeActionKey {
  SET_THEME = "SET_THEME",
}

export enum CorgiModeActionKey {
  SET_CORGI_MODE = "SET_CORGI_MODE",
}

export enum KittyModeActionKey {
  SET_KITTY_MODE = "SET_KITTY_MODE",
}

export enum ActiveEventsOpenActionKey {
  SET_ACTIVE_EVENTS_OPEN = "SET_ACTIVE_EVENTS_OPEN",
}

export enum AppActionKey {
  RESET = "RESET",
}

export enum ModalActionKey {
  SHOW_MODAL = "SHOW_MODAL",
  HIDE_MODAL = "HIDE_MODAL",
  SET_MODAL_METADATA = "SET_MODAL_METADATA",
  SET_COMMIT_DEMAND_MODAL_SHOWN = "SET_COMMIT_DEMAND_MODAL_SHOWN",
}

export enum VersionActionKey {
  FETCH_VERSIONS_STARTED = "FETCH_VERSIONS_STARTED",
  FETCH_VERSIONS_SUCCESS = "FETCH_VERSIONS_SUCCESS",
  FETCH_VERSIONS_FAILURE = "FETCH_VERSIONS_FAILURE",
}

export enum UserSecretsActionKey {
  LOADING_GCP_USER_SECRETS = "LOADING_GCP_USER_SECRETS",
  LOADING_GCP_USER_SECRET_KERNEL_ATTACHMENT = "LOADING_GCP_USER_SECRET_KERNEL_ATTACHMENT",
  SET_GCP_USER_SECRETS = "SET_GCP_USER_SECRETS",
  SET_GCP_USER_SECRET_KERNEL_ATTACHMENT = "SET_GCP_USER_SECRET_KERNEL_ATTACHMENT",
  SET_CUSTOM_USER_SECRETS = "SET_CUSTOM_USER_SECRETS",
  UPDATE_CUSTOM_USER_SECRET = "UPDATE_CUSTOM_USER_SECRET",
  SET_CUSTOM_USER_SECRET_KERNEL_ATTACHMENTS = "SET_CUSTOM_USER_SECRET_KERNEL_ATTACHMENTS",
  ADD_CUSTOM_USER_SECRET_KERNEL_ATTACHMENT = "ADD_CUSTOM_USER_SECRET_KERNEL_ATTACHMENT",
  REMOVE_CUSTOM_USER_SECRET_KERNEL_ATTACHMENT = "REMOVE_CUSTOM_USER_SECRET_KERNEL_ATTACHMENT",
}

export enum DefaultSessionSettingsActionKey {
  SET_DEFAULT_SESSION_SETTINGS = "SET_DEFAULT_SESSION_SETTINGS",
}

export enum LayoutActionKey {
  SET_LAYOUT_MODE = "SET_LAYOUT_MODE",
  SET_SIDEBAR_VIEW = "SET_SIDEBAR_VIEW",
  SET_CENTERED_LAYOUT_MODE = "SET_CENTERED_LAYOUT_MODE",
  ADD_TAB = "ADD_TAB",
  SELECT_TAB = "SELECT_TAB",
  CLOSE_TAB = "CLOSE_TAB",
}

export enum LifecycleActionKey {
  LIFECYCLE_CHANGE_OCCURRED = "LIFECYCLE_CHANGE_OCCURRED",
}

export enum LocalActionKey {
  SET_LOCAL_STATE = "SET_LOCAL_STATE",
}

export enum TimerActionKey {
  SET_SESSION_TIMEOUT_SECONDS = "SET_SESSION_TIMEOUT_SECONDS",
  SET_SESSION_IDLE_TIMEOUT_SECONDS = "SET_SESSION_IDLE_TIMEOUT_SECONDS",
  TIMER_START = "TIMER_START",
  TIMER_RESET = "TIMER_RESET",
}

export enum ToolbarActionKey {
  TOGGLE_DETAILED_STATUS_SHOWING = "TOGGLE_DETAILED_STATUS_SHOWING",
}

export enum CompetitionActionKey {
  SHOW_SUBMIT_TO_COMPETITION_MODAL = "SHOW_SUBMIT_TO_COMPETITION_MODAL",
  HIDE_SUBMIT_TO_COMPETITION_MODAL = "HIDE_SUBMIT_TO_COMPETITION_MODAL",
}

export enum ExternalSourceDataActionKey {
  SET_EXTERNAL_SOURCE_DATA = "SET_EXTERNAL_SOURCE_DATA",
}
